import React, { useEffect, useState } from "react";
import {
  Table,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Divider,
  Menu,
  Dropdown,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { Currency } from "../../types/currency.type";
import { DownOutlined } from "@ant-design/icons";
import { useAuth } from "../../contexts/auth.context";
import { canEdit } from "../../utils/roleUtils";
import { api } from "../../api/api";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";

const { Title } = Typography;
const { Option } = Select;

export default function Cashboxes() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const { data, loading, refresh } = useGetRequest(`/cashbox`);
  const { data: cashiersList, loading: cashiersListLoading } =
    useGetRequest(`/user/cashiers`);

  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      setCashboxes(data.cashboxes);
    }
  }, [data]);

  const openModal = (cashbox?: Cashbox) => {
    if (cashbox) {
      setIsEditMode(true);
      setSelectedCashbox(cashbox);
      form.setFieldsValue({
        ...cashbox,
        cashiers: cashbox.cashiers.map((cashier) => cashier.cashierId),
      });
    } else {
      setIsEditMode(false);
      setSelectedCashbox(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      if (isEditMode && selectedCashbox) {
        await api.put(`/cashbox/${selectedCashbox._id}`, values);
        message.success("Касса успешно обновлена.");
      } else {
        await api.post(`/cashbox`, values);
        message.success("Касса успешно создана.");
      }
      setIsModalOpen(false);
      setConfirmLoading(false);
      refresh();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving cashbox:", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    { title: "Название", dataIndex: "name", key: "name" },
    { title: "Валюта", dataIndex: "currency", key: "currency" },
    { title: "Курс обмена", dataIndex: "exchangeRate", key: "exchangeRate" },
    {
      title: "Баланс",
      dataIndex: "balance",
      key: "balance",
      render: (balance: number) => roundToThreeDecimals(balance),
    },
    { title: "Создал", dataIndex: "ownerName", key: "ownerName" },
    {
      title: "Кассиры",
      key: "cashiers",
      render: (_: any, cashbox: Cashbox) => (
        <ol>
          {cashbox.cashiers.map((cashier) => (
            <li key={cashier.cashierId}>{cashier.cashierName}</li>
          ))}
        </ol>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, cashbox: Cashbox) => {
        const menu = (
          <Menu>
            {canEdit(authState.user?.role) && (
              <Menu.Item onClick={() => openModal(cashbox)}>Изменить</Menu.Item>
            )}

            <Menu.Item
              onClick={() =>
                navigate(
                  `/${cashbox._id}/cashbox/${cashbox.name}/${cashbox.currency}/transactions`
                )
              }
            >
              Смотреть транзакции
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ].filter(Boolean);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={2}>Кассы</Title>
        {canEdit(authState.user?.role) && (
          <Button
            type="primary"
            onClick={() => openModal()}
            style={{ marginBottom: 16 }}
          >
            Добавить кассу
          </Button>
        )}
      </div>
      <Divider />
      <Table
        dataSource={cashboxes}
        columns={columns}
        pagination={false}
        loading={loading}
        rowKey="_id"
      />
      <Modal
        title={isEditMode ? "Изменить кассу" : "Добавить кассу"}
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название"
            rules={[
              { required: true, message: "Пожалуйста, введите название" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Пожалуйста, выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту">
              {Object.entries(Currency).map(([key, value]) => (
                <Option key={key} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="cashiers" label="Кассиры">
            <Select
              mode="multiple"
              loading={cashiersListLoading}
              placeholder="Выберите кассиров"
              optionFilterProp="children"
            >
              {cashiersList?.map((cashier: { _id: string; name: string }) => (
                <Option key={cashier._id} value={cashier._id}>
                  {cashier.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
